import { GAC_MARCA, stripGacTycTitle, tagGacTycTitle } from "../constants/gacSite";
import { ELITE_MARCA, ELITE_TYC_TITLE_PREFIX } from "../constants/eliteSite";

const META_REGEX = /^<!--\s*ev-tyc:([\s\S]*?)\s*-->/;

const parseTycMeta = (texto = "") => {
  const match = texto.match(META_REGEX);

  if (!match) {
    return {
      texto,
      activo: true,
      refId: null,
      marca: null,
    };
  }

  try {
    const meta = JSON.parse(match[1]);
    return {
      texto: texto.replace(META_REGEX, "").trim(),
      activo: meta.activo !== false,
      refId: meta.refId || null,
      marca: meta.marca || null,
    };
  } catch {
    return {
      texto: texto.replace(META_REGEX, "").trim(),
      activo: true,
      refId: null,
      marca: null,
    };
  }
};

const buildTycTextoWithMeta = (
  texto,
  { activo = true, refId, marca = GAC_MARCA } = {},
) => {
  const meta = JSON.stringify({
    activo,
    refId,
    marca,
  });

  return `<!-- ev-tyc:${meta} -->${texto}`;
};

const isEliteTycRecord = (record = {}) => {
  const parsed = parseTycMeta(record.texto || "");

  if (parsed.marca === ELITE_MARCA) {
    return true;
  }

  if (String(record.titulo || "").startsWith(ELITE_TYC_TITLE_PREFIX)) {
    return true;
  }

  const plain = `${record.titulo || ""} ${parsed.texto || ""}`.toLowerCase();
  return (
    plain.includes("elite vacances") &&
    !plain.includes("grupo alianza colombia")
  );
};

const isGacTycRecord = (record = {}) => !isEliteTycRecord(record);

const normalizeTycRecord = (record = {}) => {
  const parsed = parseTycMeta(record.texto || "");

  return {
    ...record,
    titulo: stripGacTycTitle(record.titulo || ""),
    texto: parsed.texto,
    activo: parsed.activo,
    refId: parsed.refId,
    marca: parsed.marca,
  };
};

const filterGacTycRecords = (records = []) =>
  records.filter(isGacTycRecord).map(normalizeTycRecord);

const filterEliteTycRecords = (records = []) =>
  records
    .filter(isEliteTycRecord)
    .map((record) => {
      const parsed = parseTycMeta(record.texto || "");
      return {
        ...record,
        apiId: record.id,
        id: parsed.refId || `promo-${record.id}`,
        titulo: String(record.titulo || "").replace(/^\[EV\]\s*/, "").trim(),
        texto: parsed.texto,
        activo: parsed.activo,
      };
    });

const filterActiveTycElite = (promos = []) =>
  promos.filter((promo) => promo.activo !== false);

const buildGacTycPayload = (promo = {}) => {
  const titulo = promo.titulo?.trim();
  const texto = promo.texto?.trim();

  if (!titulo) {
    throw new Error("El título es obligatorio.");
  }

  if (!texto) {
    throw new Error("El texto de términos y condiciones es obligatorio.");
  }

  return {
    titulo: tagGacTycTitle(titulo),
    texto: buildTycTextoWithMeta(texto, {
      activo: promo.activo !== false,
      refId: promo.refId || promo.id || null,
      marca: GAC_MARCA,
    }),
    marca: GAC_MARCA,
  };
};

const buildEliteTycPayload = (promo = {}) => {
  const titulo = promo.titulo?.trim();
  const texto = promo.texto?.trim();

  if (!titulo) {
    throw new Error("El título de la promoción es obligatorio.");
  }

  if (!texto) {
    throw new Error("El texto de términos y condiciones es obligatorio.");
  }

  return {
    titulo: `${ELITE_TYC_TITLE_PREFIX}${titulo.replace(/^\[EV\]\s*/, "").trim()}`,
    texto: buildTycTextoWithMeta(texto, {
      activo: promo.activo !== false,
      refId: promo.id,
      marca: ELITE_MARCA,
    }),
    marca: ELITE_MARCA,
  };
};

const isEliteTycAcceptance = (record = {}) => {
  const text = `${record.termino_texto || ""} ${record.medio_aceptacion || ""}`.toLowerCase();
  return (
    text.includes("elite vacances") &&
    !text.includes("grupo alianza colombia")
  );
};

const filterGacTycAcceptances = (records = []) =>
  records.filter((record) => !isEliteTycAcceptance(record));

export {
  parseTycMeta,
  buildTycTextoWithMeta,
  isEliteTycRecord,
  isGacTycRecord,
  normalizeTycRecord,
  filterGacTycRecords,
  filterEliteTycRecords,
  filterActiveTycElite,
  buildGacTycPayload,
  buildEliteTycPayload,
  filterGacTycAcceptances,
};
